(() => {
  const historyKey = 'glozin__recently-viewed-products';
  const historyLifetime = 30 * 24 * 60 * 60 * 1000;
  let historyTimer;
  let currentProduct;
  let productRecorded = false;

  function historyAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.preferencesProcessingAllowed() === true &&
        privacy.currentVisitorConsent().preferences === 'yes';
    } catch {
      return false;
    }
  }

  function removeHistory() {
    window.clearTimeout(historyTimer);
    try {
      localStorage.removeItem(historyKey);
    } catch { /* Storage can be unavailable in private or restricted browsers. */ }
  }

  function saveHistory(products) {
    if (!products.length) {
      removeHistory();
      return;
    }
    try {
      localStorage.setItem(historyKey, JSON.stringify({ version: 1, products }));
    } catch { /* The shopping flow must work without browser storage. */ }
  }

  function readHistory() {
    if (!historyAllowed()) return [];
    try {
      const stored = JSON.parse(localStorage.getItem(historyKey));
      if (!stored) return [];
      // Discard legacy history: its consent and age cannot be established.
      if (stored.version !== 1 || !Array.isArray(stored.products)) {
        removeHistory();
        return [];
      }
      const now = Date.now();
      const products = stored.products.filter((product) =>
        product && typeof product.id === 'string' && /^\d+$/.test(product.id) &&
        Number.isFinite(product.viewedAt) && product.viewedAt <= now &&
        product.viewedAt + historyLifetime > now
      ).slice(0, 25);
      if (products.length !== stored.products.length) saveHistory(products);
      window.clearTimeout(historyTimer);
      if (products.length) {
        const expires = Math.min(...products.map((product) => product.viewedAt)) + historyLifetime;
        historyTimer = window.setTimeout(() => {
          readHistory();
          notifyHistory();
        }, Math.min(expires - now, 2147483647));
      }
      return products;
    } catch {
      removeHistory();
      return [];
    }
  }

  function notifyHistory() {
    document.dispatchEvent(new Event('ns:recently-viewed-change'));
  }

  function recordProduct() {
    if (!currentProduct || productRecorded || !historyAllowed()) return;
    const products = readHistory().filter((product) => product.id !== currentProduct);
    products.unshift({ id: currentProduct, viewedAt: Date.now() });
    saveHistory(products.slice(0, 25));
    productRecorded = true;
    readHistory();
  }

  function syncHistory() {
    if (historyAllowed()) {
      readHistory();
      recordProduct();
    } else if (window.Shopify?.customerPrivacy) {
      removeHistory();
      productRecorded = false;
    }
    notifyHistory();
  }

  window.NordiskRecentlyViewed = {
    getIds: () => readHistory().map((product) => product.id),
    record: (id) => {
      if (!/^\d+$/.test(String(id))) return;
      if (currentProduct !== String(id)) productRecorded = false;
      currentProduct = String(id);
      recordProduct();
      notifyHistory();
    },
    clear: () => {
      removeHistory();
      notifyHistory();
    },
  };
  window.addEventListener('storage', (event) => {
    if (event.key === historyKey || event.key === null) {
      if (!historyAllowed()) removeHistory();
      notifyHistory();
    }
  });

  const trackers = [...document.querySelectorAll('script[data-consent-categories]')];
  const loaded = new Set();
  let reloading = false;

  function syncTrackers() {
    syncHistory();
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return;

    let allowed;
    try {
      allowed = {
        analytics: privacy.analyticsProcessingAllowed() === true,
        marketing: privacy.marketingAllowed() === true,
      };
    } catch {
      return; // A missing or failed API never grants consent.
    }

    const permitted = (tracker) => tracker.dataset.consentCategories.split(' ').every((category) => allowed[category] === true);

    // Removing a script element does not stop an already-running tracker.
    // Reload after Shopify has saved the withdrawal, so these scripts stay blocked.
    if ([...loaded].some((tracker) => !permitted(tracker))) {
      if (!reloading) {
        reloading = true;
        window.setTimeout(() => window.location.reload(), 0);
      }
      return;
    }

    trackers.forEach((tracker) => {
      if (loaded.has(tracker) || !permitted(tracker)) return;
      loaded.add(tracker);
      const script = document.createElement('script');
      script.async = true;
      script.src = tracker.dataset.src;
      if (tracker.dataset.key) script.dataset.key = tracker.dataset.key;
      if (tracker.dataset.consentTracker === 'gtm') {
        window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      }
      document.head.appendChild(script);
    });
  }

  function loadPrivacy() {
    if (window.Shopify?.customerPrivacy) {
      syncTrackers();
    } else {
      window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) syncTrackers();
      });
    }
  }

  document.addEventListener('visitorConsentCollected', syncTrackers);
  window.addEventListener('pageshow', loadPrivacy);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) loadPrivacy();
  });
  loadPrivacy();

  function waitForBanner() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (typeof window.privacyBanner?.showPreferences === 'function') {
          resolve(window.privacyBanner);
        } else if (++attempts < 40) {
          window.setTimeout(check, 200);
        } else {
          reject(new Error('Cookie preferences unavailable'));
        }
      };
      check();
    });
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('button[data-cookie-preferences]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    const status = document.getElementById(button.getAttribute('aria-describedby'));
    if (status) status.hidden = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const banner = await waitForBanner();
      await banner.showPreferences();
    } catch {
      if (status) status.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  });
})();
